import { useEffect, useMemo, useState } from 'react';
import EntityEditDrawer from './EntityEditDrawer';
import CoreFieldForm from './CoreFieldForm';
import CustomFieldsForm from './CustomFieldsForm';
import customFieldsApi from '../../api/customFields';
import programsApi from '../../api/programs';

const programFields = [
  { name: 'name', label: 'Program Name', required: true },
  { name: 'code', label: 'Program Code' },
  { name: 'oem', label: 'OEM / Customer', required: true },
  { name: 'platform', label: 'Platform / Nameplate' },
  {
    name: 'status',
    label: 'Status / Stage',
    type: 'select',
    options: [
      { value: 'planning', label: 'Planning' },
      { value: 'active', label: 'Active' },
      { value: 'completed', label: 'Completed' },
      { value: 'inactive', label: 'Inactive' }
    ]
  },
  { name: 'start_date', label: 'SOP', type: 'date' },
  { name: 'end_date', label: 'EOP', type: 'date' },
  { name: 'total_annual_volume', label: 'Annual Volume', type: 'number', step: '1' },
  { name: 'total_annual_value', label: 'Annual Value', type: 'number', step: '0.01' },
  { name: 'notes', label: 'Notes', type: 'textarea', span: 2 }
];

const toDateInput = (value) => value ? String(value).slice(0, 10) : '';

export default function ProgramEditDrawer({ open, onOpenChange, program, onSaved }) {
  const [formData, setFormData] = useState({});
  const [initialFormData, setInitialFormData] = useState({});
  const [customPayload, setCustomPayload] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const isCreate = !program?.id;

  useEffect(() => {
    if (!open) return;
    const nextData = {
      name: program?.name || '',
      code: program?.code || '',
      oem: program?.oem || '',
      platform: program?.platform || '',
      status: program?.status || 'planning',
      start_date: toDateInput(program?.start_date),
      end_date: toDateInput(program?.end_date),
      total_annual_volume: program?.total_annual_volume || '',
      total_annual_value: program?.total_annual_value || '',
      notes: program?.notes || ''
    };
    setFormData(nextData);
    setInitialFormData(nextData);
    setCustomPayload([]);
    setError(null);
  }, [program, open]);

  const changedFields = useMemo(() => {
    const keys = new Set([...Object.keys(initialFormData), ...Object.keys(formData)]);
    return Array.from(keys).filter((key) => String(initialFormData[key] ?? '') !== String(formData[key] ?? ''));
  }, [formData, initialFormData]);

  const hasChanges = changedFields.length > 0 || customPayload.length > 0;

  const discardChanges = () => {
    setFormData(initialFormData);
    setCustomPayload([]);
    setError(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      if (!formData.name?.trim()) {
        throw new Error('Program name is required');
      }
      if (!formData.oem?.trim()) {
        throw new Error('OEM / Customer is required');
      }

      const payload = {
        ...formData,
        total_annual_volume: formData.total_annual_volume === '' ? null : Number(formData.total_annual_volume),
        total_annual_value: formData.total_annual_value === '' ? null : Number(formData.total_annual_value)
      };

      const response = isCreate
        ? await programsApi.createProgram(payload)
        : await programsApi.updateProgram(program.id, payload);

      if (response.success === false) {
        throw new Error(response.error || response.detail || 'Failed to update program');
      }

      const savedProgramId = program?.id || response.data?.id;

      if (savedProgramId && customPayload.length > 0) {
        await customFieldsApi.setValues('program', savedProgramId, customPayload);
      }

      await onSaved?.(savedProgramId);
      setInitialFormData(formData);
      setCustomPayload([]);
      onOpenChange(false);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to save program');
    } finally {
      setSaving(false);
    }
  };

  return (
    <EntityEditDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'New Program' : 'Edit Program'}
      description={isCreate
        ? 'Create the governed program record first. Then link market forecasts, parts, planning rows, and price lifecycle data.'
        : 'Update the governed program record. Bulk volume, price, and market forecast changes should still use import or planning workflows.'
      }
      onSave={handleSave}
      saving={saving}
      error={error}
      saveLabel={isCreate ? 'Create program' : 'Save changes'}
      hasChanges={hasChanges}
      changedCount={changedFields.length + customPayload.length}
      saveDisabled={!isCreate && !hasChanges}
      onDiscard={discardChanges}
    >
      <div className="space-y-6">
        <CoreFieldForm fields={programFields} values={formData} onChange={setFormData} />
        {program?.id && (
          <CustomFieldsForm
            entityType="program"
            entityId={program.id}
            onPayloadChange={setCustomPayload}
          />
        )}
      </div>
    </EntityEditDrawer>
  );
}
